import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Users, Calculator, Trophy } from "lucide-react";

export default function CalculationPreview() {
  const navigate = useNavigate();
  const { roundId } = useParams();
  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRound();
  }, [roundId]);

  const fetchRound = async () => {
    try {
      const response = await fetch(`${API}/rounds/${roundId}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setRound(data);
      } else {
        toast.error("Ronda no encontrada");
        navigate("/dashboard");
      }
    } catch {
      
      toast.error("Error al cargar la ronda");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="w-8 h-8 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const basePlayers = round?.players?.filter(p => p.is_base) || [];
  const secondaryPlayers = round?.players?.filter(p => !p.is_base) || [];
  const baseHandicapTotal = basePlayers.reduce((sum, p) => sum + p.handicap, 0);

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(`/round/${roundId}/players`)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-[#0F172A]">Cálculos Iniciales</h1>
            <p className="text-xs text-[#64748B]">{round?.name}</p>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-8">
        {/* Base Team */}
        <Card className="card-active mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-5 h-5 text-[#1B4D3E]" />
              Pareja Base
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center mb-3">
              {basePlayers.map((player, i) => (
                <div key={player.player_id} className="text-center flex-1">
                  <p className="font-semibold text-[#0F172A]">{player.name}</p>
                  <p className="text-sm text-[#64748B]">Hcp: {player.handicap}</p>
                </div>
              ))}
            </div>
            <div className="bg-[#1B4D3E]/5 rounded-lg p-3 text-center">
              <p className="text-sm text-[#64748B]">Handicap Total</p>
              <p className="text-2xl font-bold text-[#1B4D3E]">{baseHandicapTotal}</p>
            </div>
          </CardContent>
        </Card>

        {/* Matchups */}
        <h3 className="text-lg font-bold text-[#0F172A] mb-4 flex items-center gap-2">
          <Calculator className="w-5 h-5" />
          Enfrentamientos
        </h3>

        <div className="space-y-4 mb-8">
          {round?.matchups?.map((matchup, index) => {
            const secPlayers = matchup.secondary_pair.map(id => 
              round.players.find(p => p.player_id === id)
            );
            const secTotal = secPlayers.reduce((sum, p) => sum + (p?.handicap || 0), 0);
            
            return (
              <Card key={index} className="card-default animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <CardContent className="p-4">
                  {/* Matchup Header */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="player-badge secondary">
                      Enfrentamiento {index + 1}
                    </span>
                    {matchup.handicap_advantage > 0 && (
                      <span className={`text-xs font-semibold ${matchup.advantage_to === 'base' ? 'text-[#1B4D3E]' : 'text-[#D4A373]'}`}>
                        +{matchup.handicap_advantage} golpes a {matchup.advantage_to === 'base' ? 'Base' : 'Secundarios'}
                      </span>
                    )}
                  </div>

                  {/* Teams Comparison */}
                  <div className="flex items-center justify-between">
                    {/* Base */}
                    <div className="text-center flex-1">
                      <p className="text-xs text-[#64748B] mb-1">Base</p>
                      <p className="text-sm font-medium text-[#0F172A]">
                        {basePlayers.map(p => p.name.split(' ')[0]).join(' + ')}
                      </p>
                      <p className="text-lg font-bold text-[#1B4D3E]">{baseHandicapTotal}</p>
                    </div>

                    {/* VS */}
                    <div className="px-4">
                      <span className="text-xs font-bold text-[#64748B] bg-gray-100 px-2 py-1 rounded">VS</span>
                    </div>

                    {/* Secondary */}
                    <div className="text-center flex-1">
                      <p className="text-xs text-[#64748B] mb-1">Secundarios</p>
                      <p className="text-sm font-medium text-[#0F172A]">
                        {secPlayers.map(p => p?.name?.split(' ')[0]).join(' + ')}
                      </p>
                      <p className="text-lg font-bold text-[#D4A373]">{secTotal}</p>
                    </div>
                  </div>

                  {/* Advantage Info */}
                  {matchup.handicap_advantage > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="text-xs text-[#64748B] text-center">
                        Diferencia: <strong>{matchup.handicap_advantage} golpes</strong> distribuidos en los hoyos más difíciles
                        {matchup.handicap_advantage > 18 && (
                          <span className="block mt-1 text-[#D4A373] font-semibold">
                            Doble ventaja en hoyos hcp 1-{matchup.handicap_advantage - 18}
                          </span>
                        )}
                      </p>
                      {matchup.units_advantage_total > 0 && (
                        <div className="mt-2 bg-amber-50 rounded-lg p-2 border border-amber-200">
                          <p className="text-xs text-amber-800 text-center font-semibold">
                            {matchup.units_advantage_total} unidad{matchup.units_advantage_total > 1 ? 'es' : ''} de ventaja a {matchup.units_advantage_to === 'base' ? 'Base' : 'Sec.'}
                          </p>
                          <p className="text-[10px] text-amber-600 text-center mt-0.5">
                            Compensan unidades perdidas ({matchup.handicap_advantage} ÷ {round?.units_advantage_divisor || 3} = {matchup.units_advantage_total})
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info Box */}
        <div className="bg-[#D4A373]/10 rounded-lg p-4 mb-6 border border-[#D4A373]/20">
          <p className="text-sm text-[#92400e]">
            <strong>Ventajas calculadas:</strong> Los golpes se distribuyen en los hoyos 
            con menor índice de handicap (más difíciles). Si la ventaja supera 18, los golpes extra dan doble ventaja empezando por el hoyo más difícil.
          </p>
        </div>

        <Button 
          onClick={() => navigate(`/round/${roundId}/play`)}
          className="btn-primary w-full py-6"
          data-testid="start-play-btn"
        >
          <Trophy className="w-5 h-5 mr-2" />
          Iniciar Captura de Scores
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </main>
    </div>
  );
}
